import { loadJWT } from '@/helpers/loadJWT';
import { url } from '../../api/api';
import Swal from 'sweetalert2';
import { facturasListTypes } from '@/redux/types/facturasListTypes';
import { FacturaWithRelations } from '@/types/invoices/facturaList';
import { downloadBlob } from '@/utils/downloadBlob';
import { NUMROWSTATEINVOICE } from '@/utils/constants';
import dateFormat from 'dateformat';
import axios from 'axios';

const NUM_ROWS_FACTURAS = NUMROWSTATEINVOICE;

const FILTER_INDEX_CLIENTE = 7;
const FILTER_INDEX_VENDEDOR = 8;

const buildFacturasFilter = (
  filterList: any[][],
  page: number,
  dateRange: [string, string]
): { filter: any; countFilter: any } => {
  const whereConditions: any = {
    FechaFactura: {
      between: [
        dateFormat(dateRange[0], 'isoUtcDateTime'),
        dateFormat(dateRange[1], 'isoUtcDateTime'),
      ],
    },
  };

  // Relaciones de venta para filtrado
  const ventaScope: any = {
    include: [
      { relation: 'Cliente' },
      { relation: 'Usuario' },
    ],
  };

  const ventaWhereConditions: any = {};

  if (filterList[FILTER_INDEX_CLIENTE] && filterList[FILTER_INDEX_CLIENTE][0]) {
    ventaWhereConditions.FkClientes = filterList[FILTER_INDEX_CLIENTE][0];
  }

  if (filterList[FILTER_INDEX_VENDEDOR] && filterList[FILTER_INDEX_VENDEDOR][0]) {
    ventaWhereConditions.FkUsuarios = filterList[FILTER_INDEX_VENDEDOR][0];
  }

  if (Object.keys(ventaWhereConditions).length > 0) {
    ventaScope.where = ventaWhereConditions;
  }

  const filter = {
    offset: page * NUM_ROWS_FACTURAS,
    limit: NUM_ROWS_FACTURAS,
    order: 'FechaFactura DESC',
    where: whereConditions,
    include: [
      {
        relation: 'Venta',
        scope: ventaScope,
      },
    ],
  };

  const countFilter = {
    FechaFactura: {
      between: [
        dateFormat(dateRange[0], 'isoUtcDateTime'),
        dateFormat(dateRange[1], 'isoUtcDateTime'),
      ],
    },
  };

  return { filter, countFilter };
};

export const startLoadingFacturasList = (page: number, dateRange: [string, string], filterList?: any[][]) => {
  return async (dispatch: any, getState: any) => {
    const { token } = loadJWT();
    const currentFilter = filterList || getState().facturasList.currentFilter;

    dispatch({ type: facturasListTypes.startLoadingFacturasList });

    try {
      const { filter, countFilter } = buildFacturasFilter(currentFilter, page, dateRange);

      const hasClienteFilter = currentFilter[FILTER_INDEX_CLIENTE] && currentFilter[FILTER_INDEX_CLIENTE][0];
      const hasVendedorFilter = currentFilter[FILTER_INDEX_VENDEDOR] && currentFilter[FILTER_INDEX_VENDEDOR][0];

      let facturasData: FacturaWithRelations[] = [];
      let totalCount = 0;

      if (hasClienteFilter || hasVendedorFilter) {
        // Endpoints loopback no soportan filtros anidados en count, filtrado en cliente
        const resp = await url.get(`/facturas`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { filter: JSON.stringify({ ...filter, limit: 1000, offset: 0 }) },
        });

        let filteredData = resp.data;
        
        if (hasClienteFilter) {
          filteredData = filteredData.filter(
            (f: FacturaWithRelations) => f.Venta?.Cliente?.PkClientes === currentFilter[FILTER_INDEX_CLIENTE][0]
          );
        }
        
        if (hasVendedorFilter) {
          filteredData = filteredData.filter(
            (f: FacturaWithRelations) => f.Venta?.Usuario?.PkUsuarios === currentFilter[FILTER_INDEX_VENDEDOR][0]
          );
        }

        totalCount = filteredData.length;
        facturasData = filteredData.slice(page * NUM_ROWS_FACTURAS, (page + 1) * NUM_ROWS_FACTURAS);
      } else {
        const facturasQuery = url.get(`/facturas`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { filter: JSON.stringify(filter) },
        });

        const countQuery = url.get(`/facturas/count`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { filter: JSON.stringify({ where: countFilter }) },
        });

        const [facturasResp, countResp] = await axios.all([facturasQuery, countQuery]);
        facturasData = facturasResp.data;
        totalCount = countResp.data.count;
      }

      dispatch({
        type: facturasListTypes.facturasListLoad,
        payload: facturasData.map((factura: FacturaWithRelations) => ({
          ...factura,
          TipoFactura: factura.TipoFactura === 'RECTIFICATIVA_PARCIAL' ? 'RECTIFICATIVA' : factura.TipoFactura,
        })) as FacturaWithRelations[],
      });

      dispatch({
        type: facturasListTypes.facturasListSetCount,
        payload: totalCount,
      });

      dispatch({
        type: facturasListTypes.facturasListSetPage,
        payload: page,
      });
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al cargar facturas', 'error');
    } finally {
      dispatch({ type: facturasListTypes.finishLoadingFacturasList });
    }
  };
};

export const changeFacturasListDateRange = (dateRange: [string, string]) => {
  return async (dispatch: any, getState: any) => {
    const currentFilter = getState().facturasList.currentFilter;
    dispatch({
      type: facturasListTypes.facturasListSetDateRange,
      payload: dateRange,
    });
    dispatch(startLoadingFacturasList(0, dateRange, currentFilter));
  };
};

export const changeFacturasListFilter = (filterList: any[][], page: number, dateRange: [string, string]) => {
  return async (dispatch: any) => {
    dispatch({
      type: facturasListTypes.facturasListSetFilter,
      payload: filterList,
    });
    dispatch(startLoadingFacturasList(0, dateRange, filterList));
  };
};

export const startHandlingFacturasFilter = (filterList: any[][], page: number, dateRange: [string, string]) => {
  return async (dispatch: any) => {
    dispatch({
      type: facturasListTypes.facturasListSetFilter,
      payload: filterList,
    });
    dispatch(startLoadingFacturasList(page, dateRange, filterList));
  };
};

export const resetFacturasListPage = () => {
  return {
    type: facturasListTypes.facturasListResetPage,
  };
};

// Func aux para query params de filtros csv (formato Loopback igual que GET /facturas)
const buildCSVFilterParams = (filterList: any[][], dateRange: [string, string]) => {
  const whereConditions: any = {
    FechaFactura: {
      between: [
        dateFormat(dateRange[0], 'isoUtcDateTime'),
        dateFormat(dateRange[1], 'isoUtcDateTime'),
      ],
    },
  };

  const ventaScope: any = {};
  const ventaWhereConditions: any = {};

  if (filterList[FILTER_INDEX_CLIENTE] && filterList[FILTER_INDEX_CLIENTE][0]) {
    ventaWhereConditions.FkClientes = filterList[FILTER_INDEX_CLIENTE][0];
  }
  if (filterList[FILTER_INDEX_VENDEDOR] && filterList[FILTER_INDEX_VENDEDOR][0]) {
    ventaWhereConditions.FkUsuarios = filterList[FILTER_INDEX_VENDEDOR][0];
  }

  if (Object.keys(ventaWhereConditions).length > 0) {
    ventaScope.where = ventaWhereConditions;
  }

  const filter: any = {
    where: whereConditions,
  };

  if (Object.keys(ventaScope).length > 0) {
    filter.include = [
      {
        relation: 'Venta',
        scope: ventaScope,
      },
    ];
  }

  return { filter: JSON.stringify(filter) };
};

export const downloadFacturasCSV = (filterList: any[][], dateRange: [string, string]) => {
  return async () => {
    const { token } = loadJWT();

    try {
      const params = buildCSVFilterParams(filterList, dateRange);

      const resp = await url.get(`/facturas/csv`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
        responseType: 'blob',
      });

      const fileName = `facturas_${dateFormat(new Date(), 'yyyy-mm-dd')}.csv`;
      downloadBlob(resp.data, fileName, resp.headers['content-type'] || 'text/csv');
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al descargar el CSV de facturas', 'error');
    }
  };
};

export const downloadLineasFacturadasCSV = (filterList: any[][], dateRange: [string, string]) => {
  return async () => {
    const { token } = loadJWT();

    try {
      const params = buildCSVFilterParams(filterList, dateRange);

      const resp = await url.get(`/facturas/csv-lineas-procesado`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
        responseType: 'blob',
      });

      const fileName = `lineas_facturadas_${dateFormat(new Date(), 'yyyy-mm-dd')}.csv`;
      downloadBlob(resp.data, fileName, resp.headers['content-type'] || 'text/csv');
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'Error al descargar el CSV de líneas facturadas', 'error');
    }
  };
};

